import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { AntSwitch } from '../../../components/antSwitch';
import { tableDeleteButtonSx, tableViewButtonSx } from '../../../styles/actionStyles';
import { MODELS, PAYMENT_PROVIDERS, formatMoney } from './initiativePlanRules';

// The same type, spacing and controls as the Sign-up plans table (subscriptionPlans.column.jsx).
const textSx = { fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' };
const feeLabelSx = { fontSize: 11, lineHeight: '16px', letterSpacing: '0.4px', fontWeight: 600, textTransform: 'uppercase', color: '#6C6A86', minWidth: 50 };
const feeValueSx = { fontSize: 14, lineHeight: '18px', letterSpacing: '0.15px', fontWeight: 500, color: '#1C1B1F' };
const feeMutedSx = { fontSize: 12, lineHeight: '18px', letterSpacing: '0.15px', color: '#6C6A86' };
const feeRowSx = { display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' };

const truncate = (value, max = 34) => {
  if (!value) return '-';
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

const cheapest = (tiers, field) => {
  const prices = tiers.map((t) => t[field]).filter((v) => v !== null && v !== undefined).map(Number);
  return prices.length ? Math.min(...prices) : null;
};

const feeLine = (label, value, note) => (
  <Box sx={feeRowSx}>
    <Typography component="span" sx={feeLabelSx}>{label}</Typography>
    <Typography component="span" sx={feeValueSx}>{value}</Typography>
    {note && <Typography component="span" sx={feeMutedSx}>{note}</Typography>}
  </Box>
);

/** Columns of the Initiative plans table, styled like the Sign-up plans table. */
export default function initiativePlansColumns({ onEdit, onStatus, onDelete }) {
  return [
    {
      field: 'name', headerName: 'Plan Name', flex: 1.1, minWidth: 160, sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 0.5 }}>
          <Typography sx={textSx}>{truncate(params.value, 44)}</Typography>
          <Typography sx={feeMutedSx}>{MODELS[params.row.pricingModel]?.label || params.row.pricingModel}</Typography>
        </Box>
      ),
    },
    {
      field: 'initiativeName', headerName: 'Initiative', flex: 1, minWidth: 140, sortable: false,
      renderCell: (params) => <Typography sx={textSx}>{truncate(params.value)}</Typography>,
    },
    {
      field: 'countryName', headerName: 'Country', flex: 0.9, minWidth: 120, sortable: false,
      renderCell: (params) => <Typography sx={textSx}>{truncate(params.value)}</Typography>,
    },
    {
      field: 'fees', headerName: 'Fees', flex: 1.5, minWidth: 250, sortable: false,
      renderCell: (params) => {
        const plan = params.row;
        const tiers = plan.tiers || [];
        if (plan.pricingModel === 'ENTERPRISE' && plan.enterpriseMode === 'CONTACT_US') {
          return <Typography sx={feeValueSx}>Contact us</Typography>;
        }
        const monthly = cheapest(tiers, 'monthlyPrice');
        const yearly = cheapest(tiers, 'yearlyPrice');
        const from = plan.pricingModel === 'ENTERPRISE' ? '' : 'from ';
        const note = plan.pricingModel === 'ENTERPRISE' ? '' : `${tiers.length} ${tiers.length === 1 ? 'tier' : 'tiers'}`;
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25, py: 0.75 }}>
            {feeLine('Monthly', monthly === null ? '—' : `${from}${formatMoney(monthly, plan.currency)}`, note)}
            {feeLine('Yearly', yearly === null ? '—' : `${from}${formatMoney(yearly, plan.currency)}`)}
          </Box>
        );
      },
    },
    {
      field: 'paymentProvider', headerName: 'Payment', flex: 0.9, minWidth: 130, sortable: false,
      renderCell: (params) => (
        <Typography sx={textSx}>{truncate(PAYMENT_PROVIDERS.find((p) => p.value === params.value)?.label || params.value, 20)}</Typography>
      ),
    },
    {
      field: 'status', headerName: 'Status', flex: 0.9, minWidth: 140, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
          <AntSwitch
            checked={params.row.status === 'ACTIVE'}
            onChange={() => onStatus(params.row)}
            slotProps={{ input: { 'aria-label': `${params.row.status === 'ACTIVE' ? 'Deactivate' : 'Activate'} ${params.row.name}` } }}
          />
          <Typography sx={{ ...textSx, fontWeight: 500, color: '#4F8506' }}>
            {params.row.status === 'ACTIVE' ? 'Active' : 'Inactive'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Action', flex: 1.1, minWidth: 250, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
          <Button onClick={() => onEdit(params.row)} aria-label={`Edit ${params.row.name}`}
            startIcon={<span className="material-symbols-rounded">edit</span>} sx={tableViewButtonSx}>
            Edit
          </Button>
          <Button onClick={() => onDelete(params.row)} aria-label={`Delete ${params.row.name}`}
            startIcon={<span className="material-symbols-rounded">delete</span>} sx={tableDeleteButtonSx}>
            Delete
          </Button>
        </Box>
      ),
    },
  ];
}
